import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../utils/urls";
import { BiLoaderCircle } from "react-icons/bi";

const SubscriptionManager = () => {
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false); // ✅ New state
  const [data, setData] = useState(null);
  const token = localStorage.getItem("token");

  const formatLabel = (value) => {
    if (!value) return "";
    return value
      .toString()
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      setLoading(false);
    } catch (err) {
      console.error("Subscription status fetch error:", err);
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure? Subscription remains active until the end of the billing period.")) return;

    setBtnLoading(true); // ✅ Start button loader
    await axios.post(`${API_BASE_URL}/subscription/cancel-subscription`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setBtnLoading(false); // ✅ End loader

    fetchSubscriptionStatus();
  };

  const handleReverseCancellation = async () => {
    setBtnLoading(true);
    await axios.post(`${API_BASE_URL}/subscription/reverse-cancellation`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setBtnLoading(false);
    fetchSubscriptionStatus();
  };

  const handleRestartSubscription = async () => {
    setBtnLoading(true);
    const res = await axios.post(`${API_BASE_URL}/subscription/resubscribe`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setBtnLoading(false);
    window.location.href = res.data.url;
  };

  const getStatusClasses = (status) => {
    switch (status) {
      case "active":
        return "text-green-600 bg-green-100";
      case "trialing":
        return "text-blue-600 bg-blue-100";
      case "past_due":
      case "unpaid":
      case "canceled":
      case "incomplete":
        return "text-red-600 bg-red-100";
      default:
        return "text-red-600 bg-red-100";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <BiLoaderCircle className="animate-spin text-3xl text-gray-500" />
      </div>
    );
  }

  if (!data) return null;

  const org = data.organization || {};
  const stripe = data.stripe || {};

  const nextPaymentDate = stripe.currentPeriodEnd
    ? new Date(stripe.currentPeriodEnd).toLocaleDateString()
    : null;

  const renderBtnContent = (label) => (
    btnLoading
      ? <BiLoaderCircle className="animate-spin inline-block text-lg" />
      : label
  );

  return (
    <div className="max-w-xl mx-auto mt-10 p-6 bg-white rounded-lg shadow">
      <h2 className="text-2xl font-semibold mb-5">Subscription Management</h2>

      <div className="mb-4 p-4 bg-gray-50 rounded border">
        <p><strong>Organization:</strong> {org.name || "-"}</p>
        <p><strong>Plan:</strong> {formatLabel(org.planType) || "No Plan"}</p>
        <p className="flex items-center">
          <strong className="mr-1">Status:</strong>
          <span
            className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusClasses(org.billingStatus)}`}
          >
            {formatLabel(org.billingStatus) || "-"}
          </span>
        </p>

        {nextPaymentDate && (
          <p><strong>Next Payment:</strong> {nextPaymentDate}</p>
        )}
      </div>

      <div className="space-y-3">
        {org.billingStatus === "active" && (
          <button
            onClick={handleCancelSubscription}
            disabled={btnLoading}
            className="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded flex justify-center"
          >
            {renderBtnContent("Cancel Subscription (End at period end)")}
          </button>
        )}

        {org.billingStatus === "pending_cancellation" && (
          <button
            onClick={handleReverseCancellation}
            disabled={btnLoading}
            className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-2 rounded flex justify-center"
          >
            {renderBtnContent("Reverse Cancellation")}
          </button>
        )}

        {(org.billingStatus === "expired" ||
          org.billingStatus === "canceled" ||
          org.billingStatus === "none") && (
            <button
              onClick={handleRestartSubscription}
              disabled={btnLoading}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded flex justify-center"
            >
              {renderBtnContent("Restart Subscription")}
            </button>
          )}
      </div>
    </div>
  );
};

export default SubscriptionManager;
